import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ImportRecord } from '../types';

export const useImportProcessing = () => {
  const queryClient = useQueryClient();
  const [processingRecords, setProcessingRecords] = useState<Set<string>>(new Set());

  const processImportMutation = useMutation({
    mutationFn: async (importRecord: ImportRecord) => {
      try {
        // Send the email invitation (this will handle creating invitation and updating status)
        const { data: invitationResponse, error: invitationError } = await supabase.functions.invoke('invite-user', {
          body: {
            email: importRecord.email,
            first_name: importRecord.first_name,
            last_name: importRecord.last_name,
            role: importRecord.role
          }
        });

        if (invitationError) {
          console.error('Invitation error:', invitationError);
          throw new Error(invitationError.message || 'Failed to send invitation');
        }

        // Update the import record status manually since we're not using process_user_import
        const { error: updateError } = await supabase
          .from('user_imports')
          .update({ 
            status: 'invited',
            updated_at: new Date().toISOString()
          })
          .eq('id', importRecord.id);
        
        if (updateError) {
          console.error('Failed to update import status:', updateError);
          // Don't throw here - invitation was sent successfully
        }

        return {
          invitation: invitationResponse,
          recordId: importRecord.id
        };
      } catch (error) {
        console.error('Full error in processImportMutation:', error);
        throw { ...error, recordId: importRecord.id };
      }
    },
    onMutate: (importRecord) => {
      setProcessingRecords(prev => new Set([...prev, importRecord.id]));
    },
    onSuccess: (result) => {
      setProcessingRecords(prev => {
        const newSet = new Set(prev);
        newSet.delete(result.recordId);
        return newSet;
      });
      
      if (result.invitation?.user_already_exists) {
        toast.success('User is already registered in the system');
      } else if (result.invitation?.email_id) {
        toast.success('User invitation sent successfully');
      } else {
        toast.success('Invitation created (email may have failed - check configuration)');
      }
      queryClient.invalidateQueries({ queryKey: ['user-imports'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
    onError: (error: Error & { recordId?: string }) => {
      if (error.recordId) {
        setProcessingRecords(prev => {
          const newSet = new Set(prev);
          newSet.delete(error.recordId);
          return newSet;
        });
      }
      
      console.error('Import processing error:', error);
      
      let errorMessage = 'Failed to process user invitation';
      
      if (error.message?.includes('domain')) {
        errorMessage = 'Email domain not verified. Please verify your domain in Resend.';
      } else if (error.message?.includes('RESEND_API_KEY')) {
        errorMessage = 'Email service not configured. Please contact administrator.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  });

  return {
    processImportMutation,
    processingRecords
  };
};